import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

export type MarketingAttribution = {
  source: string;
  pageType?: string | null;
  pageSlug?: string | null;
  articleSlug?: string | null;
  ctaId?: string | null;
  destination?: string | null;
  placement?: string | null;
};

const MARKETING_ATTRIBUTION_KEY = 'marketing.pendingAttribution';

function getWebStorage() {
  if (typeof localStorage === 'undefined') return null;
  if (typeof localStorage.getItem !== 'function') return null;
  if (typeof localStorage.setItem !== 'function') return null;
  if (typeof localStorage.removeItem !== 'function') return null;
  return localStorage;
}

function sanitizeAttribution(
  input: MarketingAttribution | null | undefined,
): MarketingAttribution | null {
  if (!input?.source?.trim()) return null;

  return {
    source: input.source.trim().slice(0, 80),
    pageType: input.pageType?.trim().slice(0, 80) ?? null,
    pageSlug: input.pageSlug?.trim().slice(0, 120) ?? null,
    articleSlug: input.articleSlug?.trim().slice(0, 120) ?? null,
    ctaId: input.ctaId?.trim().slice(0, 80) ?? null,
    destination: input.destination?.trim().slice(0, 80) ?? null,
    placement: input.placement?.trim().slice(0, 80) ?? null,
  };
}

export async function setPendingMarketingAttribution(
  input: MarketingAttribution | null | undefined,
): Promise<void> {
  const normalized = sanitizeAttribution(input);

  if (!normalized) {
    await clearPendingMarketingAttribution();
    return;
  }

  const serialized = JSON.stringify(normalized);

  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    if (storage) storage.setItem(MARKETING_ATTRIBUTION_KEY, serialized);
    return;
  }

  await SecureStore.setItemAsync(MARKETING_ATTRIBUTION_KEY, serialized);
}

export async function getPendingMarketingAttribution(): Promise<MarketingAttribution | null> {
  let raw: string | null = null;

  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    raw = storage?.getItem(MARKETING_ATTRIBUTION_KEY) ?? null;
  } else {
    raw = await SecureStore.getItemAsync(MARKETING_ATTRIBUTION_KEY);
  }

  if (!raw) return null;

  try {
    return sanitizeAttribution(JSON.parse(raw) as MarketingAttribution);
  } catch {
    return null;
  }
}

export async function clearPendingMarketingAttribution(): Promise<void> {
  if (Platform.OS === 'web') {
    const storage = getWebStorage();
    if (storage) storage.removeItem(MARKETING_ATTRIBUTION_KEY);
    return;
  }

  await SecureStore.deleteItemAsync(MARKETING_ATTRIBUTION_KEY);
}

export async function consumePendingMarketingAttribution(): Promise<MarketingAttribution | null> {
  const attribution = await getPendingMarketingAttribution();
  await clearPendingMarketingAttribution();
  return attribution;
}
